import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../authContext";
import { apiFetch } from "../lib/api";
import { getWorkOrders } from "../lib/wo-cache";
import { workOrderDashLabel } from "../lib/workOrderLabel";
import { formatElapsed } from "../lib/utils";
import { Button } from "./ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import TimerStopDialog from "./TimerStopDialog";
import TimerConflictDialog from "./TimerConflictDialog";
import TimerMinuteTrack from "./brand/TimerMinuteTrack";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const REPEATED_CHARS = /(.)(\1{4,})/;
const KEYBOARD_RUNS =
  /qwer|wert|erty|rtyu|tyui|yuio|uiop|asdf|sdfg|dfgh|fghj|ghjk|hjkl|zxcv|xcvb|cvbn|vbnm/i;

const validateActivityText = (text) => {
  const t = text.trim();
  if (t.length < 3) return "Note must be at least 3 characters";
  if (REPEATED_CHARS.test(t)) return "Looks like spam — please describe the actual activity";
  if (KEYBOARD_RUNS.test(t.replace(/\s+/g, ""))) return "Note appears to contain random characters";
  if (!/[a-z]/i.test(t)) return "Note must contain at least one word";
  return null;
};

function formatTodayHours(totalHours) {
  const h = Math.floor(totalHours);
  const m = Math.round((totalHours - h) * 60);
  return `${h} h ${m} m`;
}

function formatSince(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

const TimerBar = () => {
  const { activeTimer, setActiveTimer, user, timerDraft, setTimerDraft } = useAuth();

  const isRunning = !!activeTimer;

  const [elapsed, setElapsed] = useState(0);
  const [workOrders, setWorkOrders] = useState([]);
  const [todayEntries, setTodayEntries] = useState([]);
  const [timerLoading, setTimerLoading] = useState(null);
  const [stopDialog, setStopDialog] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [conflictDialog, setConflictDialog] = useState({
    open: false,
    currentTimer: null,
    pendingWO: null,
    pendingDesc: null,
  });
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!activeTimer) {
      setElapsed(0);
      return;
    }
    const tick = () => {
      const start = new Date(activeTimer.start_time).getTime();
      setElapsed(Math.floor((Date.now() - start) / 1000));
      setNow(new Date());
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [activeTimer]);

  useEffect(() => {
    if (!activeTimer && stopDialog) setStopDialog(false);
  }, [activeTimer, stopDialog]);

  const fetchWorkOrders = useCallback(async () => {
    try {
      const all = await getWorkOrders();
      setWorkOrders(all.filter((wo) => ["open", "in_progress"].includes(wo.status)));
    } catch (err) {
      console.warn("TimerBar: could not load work orders", err);
    }
  }, []);

  const fetchTodayEntries = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      const day = new Date();
      const yyyy = day.getFullYear();
      const mm = String(day.getMonth() + 1).padStart(2, "0");
      const dd = String(day.getDate()).padStart(2, "0");
      const date = `${yyyy}-${mm}-${dd}`;
      const res = await apiFetch(
        `/time-entries?start_date=${date}&end_date=${date}&user_id=${user.user_id}`
      );
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : data?.entries ?? data?.items ?? [];
      setTodayEntries(list);
    } catch (err) {
      console.warn("TimerBar: could not load today's entries", err);
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (!user) return;
    fetchWorkOrders();
    fetchTodayEntries();
  }, [user, fetchWorkOrders, fetchTodayEntries]);

  // Refresh today's entries after stop
  useEffect(() => {
    if (!isRunning) fetchTodayEntries();
  }, [isRunning, fetchTodayEntries]);

  const startTimer = async (
    workOrderId = timerDraft.workOrderId,
    desc = timerDraft.description,
    force = false
  ) => {
    if (!workOrderId) {
      toast.error("Select a work order first");
      return;
    }
    if (desc) {
      const validationError = validateActivityText(desc);
      if (validationError) {
        toast.error(validationError);
        return;
      }
    }

    if (activeTimer && !force) {
      setConflictDialog({
        open: true,
        currentTimer: activeTimer,
        pendingWO: workOrderId,
        pendingDesc: desc,
      });
      return;
    }

    setTimerLoading("starting");
    try {
      const params = new URLSearchParams({ work_order_id: workOrderId });
      if (desc) params.append("description", desc);
      if (force) params.append("stop_current", "true");

      const response = await apiFetch(`/timer/start?${params}`, { method: "POST" });

      if (response.status === 409) {
        const body = await response.json().catch(() => ({}));
        const detail = typeof body.detail === "object" ? body.detail : {};
        setConflictDialog({
          open: true,
          currentTimer: detail.current_timer ?? activeTimer,
          pendingWO: workOrderId,
          pendingDesc: desc,
        });
        return;
      }

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(typeof err.detail === "string" ? err.detail : "Failed to start timer");
      }

      const timer = await response.json();
      setActiveTimer(timer);
      setElapsed(0);
      setSwitchOpen(false);
      toast.success("Timer started!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setTimerLoading(null);
    }
  };

  const stopTimer = async (desc, activityType, attachments) => {
    if (!activeTimer) {
      setStopDialog(false);
      return;
    }
    setTimerLoading("stopping");
    try {
      const body = {};
      if (desc) body.description = desc;
      if (activityType) body.activity_type = activityType;
      if (attachments) body.github_attachments = attachments;

      const response = await apiFetch("/timer/stop", {
        method: "POST",
        body: JSON.stringify(body),
      });

      if (!response.ok) throw new Error("Failed to stop timer");

      setActiveTimer(null);
      setElapsed(0);
      setTimerDraft({ workOrderId: "", description: "" });
      setStopDialog(false);
      toast.success("Time logged successfully! ✓");
      fetchTodayEntries();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setTimerLoading(null);
    }
  };

  const activeWorkOrder = workOrders.find(
    (wo) => wo.work_order_id === activeTimer?.work_order_id
  );

  const elapsedStr = formatElapsed(elapsed);

  const trackEntries = useMemo(
    () =>
      todayEntries.map((e) => ({
        id: e.entry_id || e.id,
        start: e.start_time,
        end: e.end_time,
        hours: e.hours,
        billable: e.is_billable !== false,
      })),
    [todayEntries]
  );

  const todayHours = useMemo(() => {
    const logged = todayEntries.reduce((sum, e) => sum + (Number(e.hours) || 0), 0);
    return logged + (isRunning ? elapsed / 3600 : 0);
  }, [todayEntries, isRunning, elapsed]);

  const woTitle = activeWorkOrder
    ? workOrderDashLabel(activeWorkOrder)
    : activeTimer?.work_order_id
      ? "Work order"
      : "";
  const clientName =
    activeWorkOrder?.client_name || activeWorkOrder?.project_name || activeTimer?.client_name || "";

  const woSelect = (value, onValueChange, testId) => (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        className="h-[38px] text-sm border-brand-control bg-brand-panel shrink-0 min-w-[12rem] max-w-[32rem] flex-1"
        data-testid={testId}
      >
        <SelectValue placeholder="Choose a work order" />
      </SelectTrigger>
      <SelectContent>
        {(() => {
          const grouped = workOrders.reduce((acc, wo) => {
            const proj = wo.project_name || "No Project";
            if (!acc[proj]) acc[proj] = [];
            acc[proj].push(wo);
            return acc;
          }, {});
          return Object.entries(grouped).map(([projName, wos]) => (
            <SelectGroup key={projName}>
              <SelectLabel className="text-xs font-semibold text-brand-steel px-2 py-1">
                {projName}
              </SelectLabel>
              {wos.map((wo) => (
                <SelectItem key={wo.work_order_id} value={wo.work_order_id} className="text-sm pl-4">
                  {workOrderDashLabel(wo)}
                </SelectItem>
              ))}
            </SelectGroup>
          ));
        })()}
      </SelectContent>
    </Select>
  );

  return (
    <>
      <div
        className="h-14 border-b border-brand-border bg-brand-panel flex items-center shrink-0 w-full gap-4 md:gap-6 px-4 md:px-6"
        data-testid="timer-bar"
        role="region"
        aria-label="Timer"
      >
        {isRunning ? (
          <>
            <div className="flex items-center gap-3 w-[300px] shrink-0 min-w-0">
              <span
                className="w-2.5 h-2.5 rounded-full bg-brass shrink-0 shadow-[0_0_0_4px_rgba(184,137,59,0.20)]"
                aria-hidden
              />
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-sm font-semibold text-brand-ink truncate">{woTitle}</span>
                <span className="text-xs text-brand-steel truncate">
                  {[clientName, `running since ${formatSince(activeTimer?.start_time)}`]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              </div>
            </div>

            <TimerMinuteTrack
              entries={trackEntries}
              running={activeTimer?.start_time ? { start: activeTimer.start_time } : null}
              now={now}
              className="hidden md:block"
            />

            <span
              className="text-2xl font-semibold tracking-tight tabular-nums text-brand-ink w-[104px] text-right shrink-0"
              data-testid="timer-bar-elapsed"
            >
              {elapsedStr}
            </span>

            {switchOpen ? (
              woSelect(
                "",
                (v) => {
                  setSwitchOpen(false);
                  startTimer(v, timerDraft.description);
                },
                "timer-bar-switch-select"
              )
            ) : (
              <Button
                type="button"
                variant="outline"
                className="h-9 px-3.5 rounded-lg border-brand-control bg-transparent text-brand-ink font-medium shrink-0"
                onClick={() => setSwitchOpen(true)}
                data-testid="timer-bar-switch-btn"
              >
                Switch
              </Button>
            )}

            <Button
              type="button"
              variant="outline"
              className="h-9 px-4 rounded-lg border-danger text-danger hover:bg-danger/5 font-semibold shrink-0 gap-2"
              onClick={() => setStopDialog(true)}
              disabled={!!timerLoading}
              data-testid="timer-bar-stop-btn"
              aria-label="Stop timer"
            >
              {timerLoading === "stopping" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <span className="w-2.5 h-2.5 rounded-sm bg-danger" aria-hidden />
              )}
              Stop timer
            </Button>
          </>
        ) : (
          <>
            {/* Greeting restored (pre-§7a.3 TimerBar) — top strip, near brand */}
            <span
              className="text-[15px] font-medium text-brand-steel truncate shrink-0 max-w-[160px] md:max-w-[200px]"
              data-testid="timer-bar-greeting"
            >
              Hey,{" "}
              <span className="font-semibold text-brand-ink">
                {user?.name?.split(" ")[0] || "there"}
              </span>
            </span>

            <div className="w-px h-5 bg-brand-border shrink-0 hidden sm:block" aria-hidden />

            <span className="text-sm text-brand-steel shrink-0 hidden sm:inline" data-testid="timer-bar-idle-label">
              No timer running
            </span>

            {woSelect(
              timerDraft.workOrderId,
              (v) => setTimerDraft((d) => ({ ...d, workOrderId: v })),
              "timer-bar-wo-select"
            )}

            <span className="flex-1" />

            <span className="text-sm text-brand-steel shrink-0" data-testid="timer-bar-today-total">
              Today{" "}
              <strong className="text-brand-ink font-semibold">{formatTodayHours(todayHours)}</strong>
            </span>

            <Button
              type="button"
              className="h-[38px] px-[18px] rounded-lg bg-brand-ink text-brand-on-ink font-semibold shrink-0 hover:bg-brand-ink/90"
              onClick={() => startTimer()}
              disabled={!!timerLoading || !timerDraft.workOrderId}
              data-testid="timer-bar-start-btn"
              aria-label="Start timer"
            >
              {timerLoading === "starting" ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                "Start timer"
              )}
            </Button>
          </>
        )}
      </div>

      <TimerStopDialog
        open={stopDialog}
        elapsed={elapsedStr}
        workOrder={activeWorkOrder}
        initialDescription={activeTimer?.description}
        notes={activeTimer?.notes ?? []}
        githubAttachments={activeTimer?.github_attachments ?? []}
        loading={timerLoading === "stopping"}
        onCancel={() => setStopDialog(false)}
        onConfirm={(desc, actType, attachments) => stopTimer(desc, actType, attachments)}
      />

      <TimerConflictDialog
        open={conflictDialog.open}
        currentTimer={conflictDialog.currentTimer}
        onCancel={() =>
          setConflictDialog({ open: false, currentTimer: null, pendingWO: null, pendingDesc: null })
        }
        onStopAndStart={() => {
          const { pendingWO, pendingDesc } = conflictDialog;
          setConflictDialog({ open: false, currentTimer: null, pendingWO: null, pendingDesc: null });
          startTimer(pendingWO, pendingDesc, true);
        }}
      />
    </>
  );
};

export default TimerBar;
